import React, { useState } from 'react';
import { Bot, Sparkles } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import './AssistantDemo.css';

/** Customer: 5 scenarios. Team: 5 scenarios. */
const SCENARIOS = {
  customer: [
    {
      id: 'reschedule',
      label: 'Reschedule',
      shortLabel: 'Move appt',
      chromeSub: 'Reschedule · Sample',
      composer: 'How a customer messages your business…',
      messages: [
        {
          role: 'user',
          text: 'I need to move my appointment. Tomorrow at 2:00 will not work anymore.',
        },
        {
          role: 'assistant',
          text:
            'Your Tuesday 2:00 PM is less than 24 hours away. Per our policy, a $35 late-change fee applies before we can move or cancel that slot.\n\nReply PROCESS to accept the fee and I’ll show Thursday times. Reply KEEP to leave Tuesday booked.',
        },
        {
          role: 'user',
          text: 'PROCESS.',
        },
        {
          role: 'assistant',
          text:
            'Fee noted for checkout. You are set for Thursday at 3:00 PM. Confirmation is sent; your reminder follows your usual rules.',
        },
      ],
      points: [
        'Inside 24 hours it quotes the fee and waits for a clear yes before it moves the slot',
        'After they confirm, it offers times and finishes the change',
        'Confirmations and reminders follow your rules',
      ],
    },
    {
      id: 'new-booking',
      label: 'New booking',
      shortLabel: 'Book',
      chromeSub: 'New booking · Sample',
      composer: 'First-time or returning client booking…',
      messages: [
        {
          role: 'user',
          text: 'Do you have anything this Saturday for a first-time consult?',
        },
        {
          role: 'assistant',
          text:
            'We have 10:30 AM or 2:00 PM open. I can hold one for 15 minutes. Which works better?',
        },
        {
          role: 'user',
          text: '2 PM please.',
        },
        {
          role: 'assistant',
          text:
            "You're booked for Saturday at 2:00 PM. You'll get a confirmation text and a reminder the day before. Need directions or intake info?",
        },
      ],
      points: [
        'Turns inbound questions into holds on real slots',
        'Keeps tone and offers consistent with how you train it',
        'Sends confirmation and reminders without manual back-and-forth',
      ],
    },
    {
      id: 'hours-policy',
      label: 'Hours & policy',
      shortLabel: 'Policy',
      chromeSub: 'Policy · Sample',
      composer: 'Questions about hours, closures, or offers…',
      messages: [
        {
          role: 'user',
          text: 'Are you open next Monday if it’s a holiday?',
        },
        {
          role: 'assistant',
          text:
            "We're closed on statutory holidays, so next Monday we're closed. Tuesday opens at 9 AM. Want the next afternoon slot?",
        },
        {
          role: 'user',
          text: 'Yes, something Tuesday after 2.',
        },
        {
          role: 'assistant',
          text:
            'Tuesday has 2:30 and 4:00 PM free. I can book 2:30 and send your prep note, or add you to the waitlist for earlier. Your call.',
        },
      ],
      points: [
        'Answers hours, closures, and promos with your exact rules',
        'Sticks to the policy you set, no invented rules',
        'Moves straight to booking when the client is ready',
      ],
    },
    {
      id: 'my-visits',
      label: 'My visits',
      shortLabel: 'History',
      chromeSub: 'Visit summary · Sample',
      composer: 'Past visits, balance, next appointment…',
      messages: [
        {
          role: 'user',
          text: 'What did I get done last time and what do I owe?',
        },
        {
          role: 'assistant',
          text:
            'Last visit Feb 18: consult plus treatment on your chart. You paid $285 that day. Open balance is $120 from add-ons.\n\nNext appointment: Thu Mar 6 at 3:00 PM. Want a payment link for the balance?',
        },
        {
          role: 'user',
          text: 'Yes, send the link.',
        },
        {
          role: 'assistant',
          text:
            'Sent to the number on file. You will get a receipt when it clears. See you Mar 6.',
        },
      ],
      points: [
        'Summarizes last visit, what you paid, and balance without staff looking it up',
        'Shows your next booking in the same reply',
        'Sends payment links or prep info when you ask',
      ],
    },
    {
      id: 'appointment-followup',
      label: 'Follow-up',
      shortLabel: 'Prep',
      chromeSub: 'Reminder · Sample',
      composer: 'Reminders, prep questions, day-of…',
      messages: [
        {
          role: 'assistant',
          text:
            'Hi Jordan — reminder: you’re booked tomorrow at 3:15 PM with Priya. Reply C to confirm or RESCHEDULE if you need to move it.',
        },
        {
          role: 'user',
          text: 'What should I wear? And do I need to get there early?',
        },
        {
          role: 'assistant',
          text:
            'Wear something comfortable — no special outfit needed. Plan to arrive about 10 minutes early so we can check you in and finish intake without eating into your appointment. Bring a photo ID. Want parking or directions?',
        },
        {
          role: 'user',
          text: 'C — all good',
        },
        {
          role: 'assistant',
          text:
            'Confirmed for tomorrow 3:15 PM. See you then — message here if anything changes.',
        },
      ],
      points: [
        'Sends reminders and answers “what do I wear / how early” from your real prep rules',
        'Confirms or reschedules in the same thread without staff retyping the same answers',
        'Keeps tone and policies aligned with how you trained the assistant',
      ],
    },
  ],
  owner: [
    {
      id: 'missed-call',
      label: 'Missed call',
      shortLabel: 'Missed',
      chromeSub: 'Missed call · Sample',
      composer: 'Missed calls, follow-ups, your day…',
      messages: [
        {
          role: 'assistant',
          text:
            'You have a missed call — new lead, wanted to book this week, flexible afternoons.\n\nQuick reply:\n1) Text Thu 2:00 / Fri 10:00 slots\n2) 9 AM callback for you + SMS them the window\n3) Snooze 2h and remind me',
        },
        {
          role: 'user',
          text: '2',
        },
        {
          role: 'assistant',
          text:
            'Done. I’ll remind you at 8:55 AM; they got an SMS with the callback window and your booking link.',
        },
        {
          role: 'user',
          text: 'Missed call from a new lead. They wanted to book something this week.',
        },
        {
          role: 'assistant',
          text:
            'Summary: booking inquiry · flexible afternoons · new lead.\n\nQuick reply:\n1) Text Thu 2:00 / Fri 10:00 slots\n2) 9 AM callback for you + SMS them the window\n3) Snooze 2h and remind me',
        },
        {
          role: 'user',
          text: '2',
        },
        {
          role: 'assistant',
          text:
            'Done. They got an SMS with the callback window and your booking link. I can nudge you at 8:55 if you want a heads-up.',
        },
      ],
      points: [
        'Can ping you first with a missed-call summary and numbered options—reply with a digit and it runs the path',
        'Same thread if someone types it out: summarizes, offers slots / callback / snooze, one-tap replies',
        'Keeps the client loop closed either way',
      ],
    },
    {
      id: 'announcements',
      label: 'Announcements',
      shortLabel: 'Announce',
      chromeSub: 'Team + clients · Sample',
      composer: 'Broadcasts, schedule changes, your day…',
      messages: [
        {
          role: 'user',
          text: 'We are closing early Friday. Staff and clients with Friday appointments need the same message.',
        },
        {
          role: 'assistant',
          text:
            'Draft: early close 4 PM Friday. Same text for team SMS and client WhatsApp.\n\nQuick reply:\n1) Send now\n2) Schedule send\n3) Show draft to edit',
        },
        {
          role: 'user',
          text: '2, Thursday 6 PM',
        },
        {
          role: 'assistant',
          text:
            'Scheduled for Thursday 6 PM. Same wording for staff and clients so nothing contradicts.',
        },
        {
          type: 'divider',
          text: 'Later that day',
        },
        {
          role: 'user',
          text:
            'Send a message to everyone who has an appointment tomorrow. Let them know we have an issue in the office and we will give 10% off to make it right.',
        },
        {
          role: 'assistant',
          text:
            'Got it — only clients with appointments tomorrow.\n\nDraft:\n---\nHi from us — we are dealing with a short office issue and may run a few minutes behind. Thanks for your patience. Show this message at checkout for 10% off your visit tomorrow.\n---\n\nQuick reply:\n1) Send now\n2) Schedule send\n3) Edit wording',
        },
        {
          role: 'user',
          text: 'Fix the ending so it sounds warmer, and send at 9 AM.',
        },
        {
          role: 'assistant',
          text:
            'Updated with a warmer sign-off. Queued for tomorrow 9:00 AM to everyone on tomorrow’s book.\n\n---\nHi from us — we are dealing with a short office issue and may run a few minutes behind. We really appreciate you sticking with us. Show this at checkout for 10% off tomorrow, and thank you again for choosing us.\n---\n\nReply SEND to confirm, or EDIT to change anything else.',
        },
      ],
      points: [
        'Gives send / schedule / edit as one-tap options on every broadcast',
        'Schedules so nothing goes out at the wrong time',
        'Same voice for team and clients — plus targeted slices (e.g. tomorrow’s appointments) with draft, refine, and confirm in one thread',
      ],
    },
    {
      id: 'lead-followup',
      label: 'Lead follow-up',
      shortLabel: 'Leads',
      chromeSub: 'Follow-up · Sample',
      composer: 'Stale leads, next steps…',
      messages: [
        {
          role: 'user',
          text: 'Web lead from last week never replied. What should we do?',
        },
        {
          role: 'assistant',
          text:
            'Context: web lead · last message 3 days ago · one follow-up already sent.\n\nQuick reply:\n1) SMS with booking link (your template)\n2) Mark cold / close\n3) Assign to Sam for a call',
        },
        {
          role: 'user',
          text: '1',
        },
        {
          role: 'assistant',
          text:
            'Sent. If they reply, I’ll route it to you. If nothing in 48 hours I’ll flag it for your review.',
        },
      ],
      points: [
        'Stacks context with SMS / close / assign options you can answer in one reply',
        'Uses your templates and tone, not generic blasts',
        'Escalates when a human needs to take over',
      ],
    },
    {
      id: 'insights',
      label: 'Insights',
      shortLabel: 'Stats',
      chromeSub: 'Insights · Sample',
      composer: 'Daily stats, missed calls, a client name…',
      messages: [
        {
          role: 'user',
          text: 'How many calls came in today, how many missed, and how many clients came in?',
        },
        {
          role: 'assistant',
          text:
            'Today so far:\n• Inbound calls: 24\n• Missed: 4\n• Clients checked in: 18\n\nRough revenue from completed visits today: about $4,200.\n\nWant a breakdown by channel, or drill into one client?',
        },
        {
          role: 'user',
          text: 'What did we do for Sarah Chen last visit?',
        },
        {
          role: 'assistant',
          text:
            'Sarah Chen · last visit Mar 12: consult plus treatment per chart. $450 charged, receipt sent. Next booking Apr 2. Want a draft note for her next outreach?',
        },
      ],
      points: [
        'Daily snapshot: calls, misses, foot traffic, and rough revenue in one reply',
        'Looks up a client and shows what they had done, paid, and what is next',
        'Short answers so you are not jumping across five systems',
      ],
    },
    {
      id: 'floor-updates',
      label: 'Updates',
      shortLabel: 'Live',
      chromeSub: 'Floor updates · Sample',
      composer: 'Late arrivals, check-ins, chart notes…',
      messages: [
        {
          role: 'assistant',
          text:
            'Heads up — Alex Kim called: they’ll be about 5 minutes late for the 2:30.',
        },
        {
          role: 'assistant',
          text:
            'They’re here — checked in at the desk just now. They were actually 10 min late — put that on the file. Logged on the chart: 10 min late, arrival time updated. Anything else for Alex before we start?',
        },
        {
          role: 'user',
          text: 'They need to come back for 2 repeats add that to the plan',
        },
        {
          role: 'assistant',
          text:
            'Noted: 2 follow-up visits on file, tied to today’s treatment per your protocol. Want me to text them links to book both?',
        },
      ],
      points: [
        'Pushes late call and check-in updates back-to-back so the room sees the full picture fast',
        'Writes arrival and late details to the chart without a separate back-and-forth',
        'You add plan notes (repeats, rebooks) in one reply; it confirms and offers next steps',
      ],
    },
  ],
};

const CHANNEL_CHIPS = ['WhatsApp Business', 'SMS', 'Email', 'iMessage'];

const MODES = [
  {
    id: 'customer',
    label: 'Clients & prospects',
    shortLabel: 'Clients',
    ariaLabel: 'Customer-facing assistant demo',
  },
  {
    id: 'owner',
    label: 'You & your team',
    shortLabel: 'Team',
    ariaLabel: 'Owner and team assistant demo',
  },
];

const AssistantDemo = () => {
  const [ref, visible] = useScrollReveal();
  const [mode, setMode] = useState('customer');
  const [scenarioByMode, setScenarioByMode] = useState({ customer: 0, owner: 0 });

  const scenarios = SCENARIOS[mode];
  const scenarioIndex = scenarioByMode[mode];
  const activeScenario = scenarios[scenarioIndex] || scenarios[0];

  const setScenarioIndex = (index) => {
    setScenarioByMode((prev) => ({ ...prev, [mode]: index }));
  };

  const messages = activeScenario.messages;
  const points = activeScenario.points;

  const modeMeta = MODES.find((m) => m.id === mode) || MODES[0];

  return (
    <section id="ai-assistant" className="assistant-demo section" aria-labelledby="ai-assistant-heading">
      <div className="container">
        <div className="section-header">
          <p className="section-kicker">Personal AI Business Assistant</p>
          <h2 id="ai-assistant-heading" className="section-title">
            One assistant for your customers and your operations
          </h2>
          <p className="section-subtitle">
            Client chats and team updates in one place. Route changes to the right people. Push announcements when
            things shift. Clients do not need to chase you app to app.
          </p>
        </div>

        <div
          className="assistant-demo-toggle-wrap"
          role="tablist"
          aria-label="Choose assistant demo: customer or owner"
        >
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              id={`assistant-tab-${m.id}`}
              aria-selected={mode === m.id}
              aria-controls="assistant-demo-panel"
              className={`assistant-demo-toggle ${mode === m.id ? 'is-active' : ''}`}
              onClick={() => setMode(m.id)}
            >
              <span className="assistant-demo-toggle-full">{m.label}</span>
              <span className="assistant-demo-toggle-short">{m.shortLabel}</span>
            </button>
          ))}
        </div>

        <div
          className="assistant-demo-scenario-wrap"
          role="tablist"
          aria-label={`Demo scenarios for ${mode === 'customer' ? 'clients' : 'team'}`}
        >
          {scenarios.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              id={`assistant-scenario-${mode}-${s.id}`}
              aria-selected={scenarioIndex === i}
              aria-controls="assistant-demo-panel"
              className={`assistant-demo-scenario ${scenarioIndex === i ? 'is-active' : ''}`}
              onClick={() => setScenarioIndex(i)}
            >
              <span className="assistant-demo-scenario-full">{s.label}</span>
              <span className="assistant-demo-scenario-short">{s.shortLabel}</span>
            </button>
          ))}
        </div>

        <div
          ref={ref}
          id="assistant-demo-panel"
          role="tabpanel"
          aria-labelledby={`assistant-tab-${mode}`}
          className={`assistant-demo-block reveal ${visible ? 'is-visible' : ''}`}
        >
          <div className="assistant-demo-layout">
            <div className="assistant-demo-chrome">
              <div className="assistant-demo-chrome-top">
                <div className="assistant-demo-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="assistant-demo-chrome-title">
                  <span className="assistant-demo-chrome-icon" aria-hidden="true">
                    <Bot size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <span className="assistant-demo-chrome-name">Cure8 Assistant</span>
                    <span className="assistant-demo-chrome-sub">Your business · {activeScenario.chromeSub}</span>
                  </div>
                </div>
                <span className="assistant-demo-live">
                  <Sparkles size={12} aria-hidden="true" /> Demo
                </span>
              </div>

              <div
                key={`${mode}-${activeScenario.id}`}
                className="assistant-demo-thread"
                role="log"
                aria-label={modeMeta.ariaLabel}
              >
                {messages.map((msg, i) => (
                  msg.type === 'divider' ? (
                    <div key={`${activeScenario.id}-${i}`} className="assistant-demo-divider" aria-hidden="true">
                      <span>{msg.text}</span>
                    </div>
                  ) : (
                    <div
                      key={`${activeScenario.id}-${i}`}
                      className={`assistant-demo-msg assistant-demo-msg--${msg.role}`}
                      style={{ animationDelay: `${i * 0.06}s` }}
                    >
                      <div className="assistant-demo-bubble">{msg.text}</div>
                    </div>
                  )
                ))}
              </div>

              <div className="assistant-demo-composer" aria-hidden="true">
                <div className="assistant-demo-composer-inner">
                  <span className="assistant-demo-placeholder">{activeScenario.composer}</span>
                  <span className="assistant-demo-send">Send</span>
                </div>
              </div>
            </div>

            <ul className="assistant-demo-points" key={`${mode}-${activeScenario.id}`}>
              {points.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="assistant-demo-integrations">
            <p className="assistant-demo-int-label">Same assistant on the channels you use</p>
            <ul className="assistant-demo-int-chips" aria-label="Example channels and systems">
              {CHANNEL_CHIPS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssistantDemo;
